import { useTranslation } from "react-i18next";

import type { CaseListFilters } from "@/api/endpoints/build-case-report-metadata";
import { Button } from "@/components/ui/button";

import {
  useCreateSavedView,
  useDeleteSavedView,
  useSavedViews,
} from "./use-saved-views";

export type SavedViewBarProps = {
  readonly filters: CaseListFilters;
  readonly onApply: (_filters: CaseListFilters) => void;
};

/**
 * 一覧の絞り込みを名前付きで保存・呼び出す（画面上は「ビュー」）。
 * 独立メニューにはせず、一覧ごとに置く（issue #29 の確定仕様）。
 */
export function SavedViewBar({ filters, onApply }: SavedViewBarProps) {
  const { t } = useTranslation("common");
  const views = useSavedViews();
  const create = useCreateSavedView();
  const remove = useDeleteSavedView();

  const hasCondition =
    filters.keyword.trim() !== "" ||
    filters.statuses.length > 0 ||
    filters.processModelId !== null ||
    filters.submittedFrom !== null ||
    filters.submittedTo !== null;

  const save = () => {
    // 保存は稀な操作なので、専用ダイアログを持たず prompt で済ませる
    const name = window.prompt(t("savedViews.namePrompt"));
    if (name === null || name.trim() === "") return;
    create.mutate({ name: name.trim(), filters });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground text-xs font-medium">
        {t("savedViews.label")}
      </span>

      {views.data?.length === 0 ? (
        <span className="text-muted-foreground text-xs">
          {t("savedViews.empty")}
        </span>
      ) : null}

      {views.data?.map((view) => (
        <span
          key={view.id}
          className="border-border flex items-center gap-1 rounded-full border pr-1 pl-1"
        >
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 rounded-full"
            onClick={() => onApply(view.filters)}
          >
            {view.name}
          </Button>
          <button
            type="button"
            aria-label={`${view.name} ${t("savedViews.delete")}`}
            className="text-muted-foreground hover:text-destructive px-1 text-xs"
            onClick={() => remove.mutate(view.id)}
          >
            ×
          </button>
        </span>
      ))}

      {hasCondition ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={create.isPending}
          onClick={save}
        >
          {t("savedViews.save")}
        </Button>
      ) : null}
    </div>
  );
}
